import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiCheck, FiDownload } from 'react-icons/fi';
import { useAuthStore } from '../../store/authStore';
import { useLanguage } from '../../contexts/LanguageContext';
import { GAMEGUARD_URL } from '../../lib/constants';
import BrandMark from '../../components/Layout/BrandMark';
import LanguageMenu from '../../components/UI/LanguageMenu';
import SocialLinks from '../../components/UI/SocialLinks';
import { Action } from '../MVP/shared';
import { mapImage } from '../../lib/mapImages';
import LandingShowcase from './LandingShowcase';
import LegalLinks from '../../legal/LegalLinks';

/* Публичная страница: лёгкая шапка, один герой, несколько блоков разной композиции.
   Все блоки описывают только работающие функции; контент виден без анимации появления. */
const Page = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.text};
`;
const Wrap = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 clamp(16px, 4vw, 32px);
`;
const Top = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 64px;
  .right {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  @media (max-width: 600px) {
    height: 56px;
    .hide-sm {
      display: none;
    }
  }
`;
const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 32px;
  padding: clamp(24px, 6vw, 72px) 0;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 24px 0 32px;
  }
  .eyebrow {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.primary};
  }
  h1 {
    font-size: clamp(40px, 6vw, 64px);
    line-height: 1.05;
    font-weight: 750;
    letter-spacing: -0.03em;
    max-width: 12ch;
  }
  .sub {
    margin-top: 20px;
    max-width: 46ch;
    font-size: 18px;
    line-height: 28px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .cta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 28px;
  }
`;
const Block = styled.section<{ $flip?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 48px;
  align-items: center;
  padding: clamp(32px, 6vw, 72px) 0;
  .text {
    order: ${(p) => (p.$flip ? 2 : 1)};
  }
  .visual {
    order: ${(p) => (p.$flip ? 1 : 2)};
  }
  h2 {
    font-size: clamp(26px, 3.4vw, 36px);
    line-height: 1.15;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  p {
    margin-top: 16px;
    max-width: 52ch;
    font-size: 17px;
    line-height: 26px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  ul {
    list-style: none;
    display: grid;
    gap: 10px;
    margin-top: 20px;
  }
  li {
    display: flex;
    gap: 12px;
    align-items: center;
    font-size: 16px;
    svg {
      flex: 0 0 auto;
      color: ${(p) => p.theme.colors.primary};
    }
  }
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    .text,
    .visual {
      order: initial;
    }
  }
`;
const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  figure {
    margin: 0;
    overflow: hidden;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.surface};
  }
  img {
    display: block;
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
  }
  figcaption {
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 600;
  }
`;
const Skin = styled.div`
  display: grid;
  place-items: center;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  img {
    width: min(100%, 420px);
    height: auto;
  }
`;
const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  div {
    padding: 20px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.surface};
  }
  dt {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  dd {
    margin-top: 4px;
    font-size: 18px;
    font-weight: 650;
  }
`;
const Steps = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  counter-reset: step;
  li {
    display: grid;
    gap: 8px;
    font-size: 16px;
    line-height: 24px;
    counter-increment: step;
    &::before {
      content: counter(step);
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.primary};
      font-weight: 700;
    }
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Section = styled.section`
  padding: clamp(32px, 6vw, 64px) 0;
  h2 {
    font-size: clamp(24px, 3vw, 32px);
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 24px;
  }
  > p {
    margin: -12px 0 24px;
    max-width: 56ch;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;
const MapChips = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  li {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 16px 0 6px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.surface};
    font-size: 15px;
    font-weight: 600;
  }
  li:not(.has-img) {
    padding-left: 16px;
  }
  img {
    width: 52px;
    height: 32px;
    object-fit: cover;
    border-radius: 8px;
  }
`;
const Final = styled.section`
  display: grid;
  justify-items: start;
  gap: 8px;
  margin: clamp(24px, 5vw, 48px) 0;
  padding: clamp(24px, 5vw, 48px);
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  h2 {
    font-size: clamp(26px, 3.4vw, 36px);
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  p {
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .cta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
  }
`;
const Foot = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 0 40px;
  .links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 20px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textMuted};
    a {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      color: ${(p) => p.theme.colors.textSecondary};
    }
    a:hover {
      color: ${(p) => p.theme.colors.text};
      text-decoration: underline;
    }
  }
`;

const MAPS = ['Anubis', 'Ancient', 'Inferno', 'Mirage', 'Nuke', 'Overpass', 'Dust II', 'Vertigo'];

export default function Landing() {
  const { t } = useLanguage();
  const authed = useAuthStore((s) => s.isAuthenticated);
  const start = authed ? '/' : '/login';

  return (
    <Page>
      <Wrap>
        <Top>
          <BrandMark />
          <div className="right">
            <LanguageMenu />
            <Action as="a" $variant="ghost" href={GAMEGUARD_URL} target="_blank" rel="noopener noreferrer" className="hide-sm">
              <FiDownload aria-hidden="true" />
              {t('land.download')}
            </Action>
            {!authed && (
              <>
                <Action as={Link} to="/login" $variant="ghost">
                  {t('land.login')}
                </Action>
                <Action as={Link} to="/register" $variant="primary" className="hide-sm">
                  {t('land.register')}
                </Action>
              </>
            )}
          </div>
        </Top>

        <Hero>
          <div>
            <p className="eyebrow">{t('land.eyebrow')}</p>
            <h1>{t('land.h1')}</h1>
            <p className="sub">{t('land.sub')}</p>
            <div className="cta">
              <Action as={Link} to={start} $variant="primary" style={{ minHeight: 52, paddingInline: 28 }}>
                {authed ? t('land.cta_in') : t('land.cta')}
              </Action>
            </div>
          </div>
          <LandingShowcase />
        </Hero>

        <Block>
          <div className="text">
            <h2>{t('land.b1_title')}</h2>
            <p>{t('land.b1_text')}</p>
            <ul>
              {[t('land.b1_li1'), t('land.b1_li2'), t('land.b1_li3')].map((x) => (
                <li key={x}>
                  <FiCheck aria-hidden="true" />
                  {x}
                </li>
              ))}
            </ul>
          </div>
          <Tiles className="visual" aria-hidden="true">
            {(['dust2', 'nuke', 'overpass'] as const).map((m) => (
              <figure key={m}>
                <img src={mapImage(m) || ''} alt="" loading="lazy" />
                <figcaption>{m === 'dust2' ? 'Dust II' : m === 'nuke' ? 'Nuke' : 'Overpass'}</figcaption>
              </figure>
            ))}
          </Tiles>
        </Block>

        <Block $flip>
          <div className="text">
            <h2>{t('land.b2_title')}</h2>
            <p>{t('land.b2_text')}</p>
            <ul>
              {[t('land.b2_li1'), t('land.b2_li2'), t('land.b2_li3')].map((x) => (
                <li key={x}>
                  <FiCheck aria-hidden="true" />
                  {x}
                </li>
              ))}
            </ul>
          </div>
          <Facts className="visual">
            <div>
              <dt>ELO</dt>
              <dd>{t('land.b2_f1')}</dd>
            </div>
            <div>
              <dt>K/D</dt>
              <dd>{t('land.b2_f2')}</dd>
            </div>
            <div>
              <dt>{t('stats.winrate')}</dt>
              <dd>{t('land.b2_f3')}</dd>
            </div>
            <div>
              <dt>{t('stats.table.title')}</dt>
              <dd>{t('land.b2_f4')}</dd>
            </div>
          </Facts>
        </Block>

        <Block>
          <div className="text">
            <h2>{t('land.b3_title')}</h2>
            <p>{t('land.b3_text')}</p>
          </div>
          <Skin className="visual" aria-hidden="true">
            <img src="/landing/ak47.png" alt="" loading="lazy" width="512" height="384" />
          </Skin>
        </Block>

        <Section aria-labelledby="steps-title">
          <h2 id="steps-title">{t('land.steps_title')}</h2>
          <Steps>
            {[t('land.step1'), t('land.step2'), t('land.step3'), t('land.step4')].map((x) => (
              <li key={x}>{x}</li>
            ))}
          </Steps>
        </Section>

        <Section aria-labelledby="maps-title">
          <h2 id="maps-title">{t('land.maps_title')}</h2>
          <p>{t('land.maps_text')}</p>
          <MapChips>
            {MAPS.map((m) => {
              const img = mapImage(m.replace(' II', '2'));
              return (
                <li key={m} className={img ? 'has-img' : undefined}>
                  {img && <img src={img} alt="" loading="lazy" />}
                  {m}
                </li>
              );
            })}
          </MapChips>
        </Section>

        <Final>
          <h2>{t('land.final_title')}</h2>
          <p>{t('land.final_text')}</p>
          <div className="cta">
            <Action as={Link} to={authed ? '/' : '/register'} $variant="primary" style={{ minHeight: 52, paddingInline: 28 }}>
              {authed ? t('land.cta_in') : t('land.register')}
            </Action>
            {!authed && (
              <Action as={Link} to="/login" style={{ minHeight: 52 }}>
                {t('land.login')}
              </Action>
            )}
          </div>
        </Final>

        <Foot>
          <SocialLinks />
          <div className="links">
            <span>© {new Date().getFullYear()} CS Hub</span>
          </div>
        </Foot>
        <LegalLinks />
      </Wrap>
    </Page>
  );
}
